import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "@/axiosInstance"; // Import axiosInstance

// Fetch Cart Items
export const fetchCartItems = createAsyncThunk(
  "cart/fetchCartItems",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/api/website/cart");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

// Add to Cart
export const addToCart = createAsyncThunk(
  "cart/addToCart",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/api/website/cart/add", payload);
      return response.data; // Return the cart item data
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

// Delete Cart Item
export const deleteCartItem = createAsyncThunk(
  "cart/deleteCartItem",
  async (cartItemId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(`/api/website/cart/item/${cartItemId}`);
      return { cartItemId, ...response.data }; // Return the deleted cart item ID
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

// Update Cart Item Quantity
export const updateCartItemQuantity = createAsyncThunk(
  "cart/updateCartItemQuantity",
  async ({ cartItemId, quantity, variantId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `/api/website/cart/item/${cartItemId}`,
        { quantity, variant_id: variantId } // Request body
      );
      return response.data; // Return updated cart data
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

const cartSlice = createSlice({
  name: "cart",
  initialState: {
    cartItems: [],
    loading: false,
    error: null,
  },
  reducers: {
    updateQuantityOptimistic: (state, action) => {
      const { cartItemId, newQuantity } = action.payload;
      if (state.cartItems?.cart?.items) {
        const updatedItems = state.cartItems.cart.items.map(item =>
          item.cart_item_id === cartItemId
            ? { ...item, quantity: newQuantity }
            : item
        );
        state.cartItems.cart.items = updatedItems;
      }
    },
    deleteCartItemOptimistic: (state, action) => {
      const cartItemId = action.payload;
      if (state.cartItems?.cart?.items) {
        state.cartItems.cart.items = state.cartItems.cart.items.filter(
          item => item.cart_item_id !== cartItemId
        );
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Add to Cart
      .addCase(addToCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addToCart.fulfilled, (state, action) => {
        state.loading = false;
        
        // Ensure cartItems exists
        if (!state.cartItems?.cart?.items) {
          state.cartItems = { cart: { items: [] } };
        }
    
        state.cartItems.cart.items.push(action.payload);
    })
    
      .addCase(addToCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Cart Items
      .addCase(fetchCartItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCartItems.fulfilled, (state, action) => {
        state.loading = false;
        state.cartItems = action.payload;
      })
      .addCase(fetchCartItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete Cart Item
      .addCase(deleteCartItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCartItem.fulfilled, (state, action) => {
        state.loading = false;
      
        // Ensure cartItems exists and update only the items list
        if (state.cartItems?.cart?.items) {
          state.cartItems.cart.items = state.cartItems.cart.items.filter(
            item => item.cart_item_id !== action.payload.cartItemId
          );
        }
      })
      
      .addCase(deleteCartItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Cart Item Quantity
      .addCase(updateCartItemQuantity.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCartItemQuantity.fulfilled, (state, action) => {
        const { cart_item_id, quantity } = action.payload;
        if (state.cartItems?.cart?.items) {
          state.cartItems.cart.items = state.cartItems.cart.items.map(item =>
            item.cart_item_id === cart_item_id
              ? { ...item, quantity } // Ensure API response updates the final state
              : item
          );
        }
      })
      .addCase(updateCartItemQuantity.rejected, (state, action) => {
        console.error("API call failed, but UI was already updated:", action.payload);
        state.error = action.payload;
      });
  },
});
export const { updateQuantityOptimistic, deleteCartItemOptimistic  } = cartSlice.actions;
export default cartSlice.reducer;
